<script>
    import { onMount, tick } from 'svelte';
    import { loadGoogleMaps, createPinElement } from '../utils/googleMaps';
    import { env } from '../Environment.svelte';
    import { formValidation, mapFields } from '../Validators.svelte';
    import { store, store_offer } from '../Store.svelte';
    import { scrollToTop } from './comparator/utils/utils';
    import MapInput from '../components/MapInput.svelte';
    import FullScreenLoader from '../components/FullScreenLoader.svelte';
    import { validateOffer } from '../clients/ValidateClientV2.svelte';
    // import { getOfferV2 } from '../clients/OfferClientV2.svelte';
    export let toggleEditAddress = () => {};
    let maxRetries = 5;
    let retries = 0;
    let isLoading = false;
    let map, marker, geocoder, autocompleteCP, autocompleteAddress;
    let error = '';
    let errorGeolocation = '';
    let postalCode = '';
    let address = '';
    let neighborhood = '';
    let city = '';
    let state = '';
    let interior = '';
    let exterior = '';
    let renderSpecificFields = false;
    let lastPosition = {
        lat: 0,
        lng: 0,
    };
    let fields = mapFields;
    let errorMessage = '';

    const ERROR_DELTA = 0.0005;

    const handleMapClick = (event) => {
        placeMarker(event.latLng, true);
    };

    const handlePlaceChanged = (type, inputCP) => {
        let place;
        if (type === 'CP') {
            fields.postalCode.error = false;
            fields = fields;
            if (
                autocompleteCP.getPlace() !== undefined &&
                inputCP == undefined
            ) {
                place = autocompleteCP.getPlace();
            } else {
                place = { name: inputCP.value };
            }
            if (!place.geometry) {
                geocoder.geocode(
                    {
                        componentRestrictions: {
                            postalCode: place.name,
                            country: 'MX',
                        },
                    },
                    (results, status) => {
                        if (status === 'ZERO_RESULTS') {
                            fields.postalCode.error = true;
                            fields = fields;
                            return;
                        }
                        if (status === 'OK' && results[0]) {
                            placeMarker(results[0].geometry.location);
                            fields.postalCode.error = false;
                            fields = fields;
                        }
                    }
                );
                return;
            }
        } else if (type === 'address') {
            place = autocompleteAddress.getPlace();
            if (!place.geometry) {
                geocoder.geocode(
                    {
                        address: place.name,
                        componentRestrictions: {
                            country: 'MX',
                        },
                    },
                    (results, status) => {
                        if (status === 'OK' && results[0]) {
                            placeMarker(results[0].geometry.location);
                        }
                    }
                );
                return;
            }
        }
        if (!place.geometry) {
            error = 'No se encontró la dirección';
            return;
        }
        placeMarker(place.geometry.location);
        postalCode = place.address_components.find((component) =>
            component.types.includes('postal_code')
        ).long_name;
    };

    const initializeAutocompleteAddress = () => {
        const inputAddress = document.getElementById('autocompleteAddress');
        if (inputAddress) {
            autocompleteAddress = new google.maps.places.Autocomplete(
                inputAddress,
                {
                    types: ['geocode'],
                    componentRestrictions: { country: 'MX' },
                }
            );
        }
        if (autocompleteAddress) {
            autocompleteAddress.addListener('place_changed', () => {
                handlePlaceChanged('address');
            });
        }
    };

    const getMarkerPosition = () => {
        const position = marker.getPosition();
        const lat = position.lat();
        const lng = position.lng();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
                updateAddressInfo(results[0]);
            }
        });
    };

    const initializeAutocompletePostalCode = () => {
        const inputCP = document.getElementById('autocompleteCP');
        if (inputCP) {
            autocompleteCP = new google.maps.places.Autocomplete(inputCP, {
                types: ['postal_code'],
                componentRestrictions: { country: 'MX' },
            });
            inputCP.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                }
            });
            inputCP.addEventListener('blur', (e) => {
                handlePlaceChanged('CP', e.target);
            });
        }
        if (autocompleteCP) {
            autocompleteCP.addListener('place_changed', () => {
                handlePlaceChanged('CP');
            });
        }
    };

    $: if (renderSpecificFields) {
        tick().then(() => {
            initializeAutocompletePostalCode();
        });
    }

    const initializeMap = (
        coordinates = { lat: 19.4326077, lng: -99.133208 }
    ) => {
        const { lat, lng } = coordinates;
        const mapOptions = {
            center: { lat, lng },
            zoom: 15,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false,
            restriction: {
                latLngBounds: {
                    north: 32.72,
                    south: 14.54,
                    west: -118.45,
                    east: -86.7,
                },
                strictBounds: true,
            },
        };
        map = new google.maps.Map(document.getElementById('map'), mapOptions);
        map.addListener('click', handleMapClick);
    };

    const placeMarker = (location, click) => {
        if (!marker) {
            marker = new google.maps.Marker({
                position: location,
                map,
                draggable: true,
                content: createPinElement(),
            });
            marker.addListener('dragend', getMarkerPosition);
        } else {
            marker.setPosition(location);
        }
        if (!click) {
            map.setCenter(location);
        }
        getMarkerPosition();
    };

    const updateAddressInfo = (result) => {
        if (!result?.address_components) return;

        const postalCodeComponent = result.address_components.find(
            (component) => component.types.includes('postal_code')
        );
        const newPostalCode = postalCodeComponent?.long_name;

        const deltaLat = Math.abs(
            lastPosition.lat - marker.getPosition().lat()
        );
        const deltaLng = Math.abs(
            lastPosition.lng - marker.getPosition().lng()
        );

        if (
            (newPostalCode && newPostalCode !== postalCode) ||
            deltaLat >= ERROR_DELTA ||
            deltaLng >= ERROR_DELTA
        ) {
            lastPosition = {
                lat: marker.getPosition().lat(),
                lng: marker.getPosition().lng(),
            };

            const routeComponent = result.address_components.find((component) =>
                component.types.includes('route')
            );
            const streetNumberComponent = result.address_components.find(
                (component) => component.types.includes('street_number')
            );

            const addressComponent = routeComponent?.long_name || 'Desconocida';

            handleAddressChange({ target: { value: addressComponent } });

            const neighborhoodComponent = result.address_components.find(
                (component) => component.types.includes('sublocality')
            )?.long_name;
            const cityComponent = result.address_components.find((component) =>
                component.types.includes('locality')
            )?.long_name;
            const stateComponent = result.address_components.find((component) =>
                component.types.includes('administrative_area_level_1')
            )?.long_name;
            if (neighborhoodComponent) neighborhood = neighborhoodComponent;
            if (streetNumberComponent)
                exterior = streetNumberComponent.long_name;
            if (cityComponent) city = cityComponent;
            if (stateComponent) state = stateComponent;
            if (newPostalCode) postalCode = newPostalCode;
            if (addressComponent.trim()) address = addressComponent;
        }
    };

    const handleCurrentPosition = () => {
        if (!navigator.geolocation) {
            errorGeolocation = 'No pudimos determinar tu ubicación actual';
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                errorGeolocation = '';
                const coordinates = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                placeMarker(coordinates);
            },
            (err) => {
                errorGeolocation = 'No pudimos determinar tu ubicación actual';
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
    };

    onMount(() => {
        void init();
    });

    async function init() {
        await loadGoogleMaps();
        if (marker) {
            marker.setMap(null);
            marker = null;
        }
        if (
            typeof google !== 'undefined' &&
            google.maps &&
            google.maps.places
        ) {
            geocoder = new google.maps.Geocoder();
            initializeAutocompleteAddress();
            initializeMap();
            if (lastPosition.lat !== 0 && lastPosition.lng !== 0) {
                placeMarker({
                    lat: lastPosition.lat,
                    lng: lastPosition.lng,
                });
            }
            loadAddressFromStore();
        } else if (retries < maxRetries) {
            retries++;
            setTimeout(init, 1000);
        } else {
            console.error('Google maps could not be loaded');
            errorMessage =
                'Hubo un error al cargar el mapa. Por favor, intenta de nuevo.';
        }
    }

    function processOffers(offersData) {
        if (!offersData || !offersData.offers) return;
        const extrasMapNames = [
            'facturacion',
            'graba',
            'movil2_0',
            'canalesPremium',
            'extensiones',
            'telefonoExtensiones',
            'telefono',
            'internet',
            'canalesCarta',
            'ott',
        ];
        makeExtra(offersData);

        Object.values(offersData.offers).forEach((offer) => {
            offer.forEach((packet) => {
                if (packet.extrasMap) {
                    Object.entries(packet.extrasMap).forEach(
                        ([extraType, extras]) => {
                            packet.extrasMap[extraType] = extras.map(
                                (extraIdReference) =>
                                    offersData.extras[extraIdReference]
                            );
                        }
                    );
                    extrasMapNames.forEach((extraName) => {
                        if (!packet.extrasMap[extraName]) {
                            packet.extrasMap[extraName] = [];
                        }
                    });
                }
            });
        });
    }
    const makeExtra = (coverage) => {
        coverage.extras = Object.fromEntries(
            Object.entries(coverage.extras).map(([key, extra]) => [
                key,
                {
                    extId: extra.extId,
                    paqextMaximo: extra.paqextMaximo,
                    paqextOrden: extra.paqextOrden,
                    paqextQuitar: extra.paqextQuitar,
                    paqextVisible: extra.paqextVisible,
                    paqextDefault: extra.paqextDefault,
                    paquetesVisible: extra.paquetesVisible,
                    extra: {
                        cost: extra.cost,
                        promoCost: extra.promoCost,
                        precioSP: extra.precioSP,
                        promoCostViX: extra.promoCostViX,
                        extEliminar:
                            extra.extEliminar && extra.extEliminar.length > 0
                                ? extra.extEliminar
                                : null,
                        extRequired:
                            extra.extRequired && extra.extRequired.length > 0
                                ? extra.extRequired
                                : null,
                        extGrupo: extra.extGrupo ? extra.extGrupo : null,
                        subtitle: extra.description,
                        title: extra.title,
                        extName: extra.extName,
                        promoItem: extra.promoItem ? extra.promoItem : null,
                        opcionesModular: extra.opcionesModular,
                        descriptionOTT: extra.descriptionOTT,
                    },
                },
            ])
        );
    };

    const proxyOffer = (offerData) => {
        if (!offerData) {
            return;
        }
        const offnetOffers = offerData[0];
        const onnetOffers = offerData[1];
        processOffers(onnetOffers);
        processOffers(offnetOffers);
        //load to store
        $store_offer.loadedOffer = {
            coverageType: offnetOffers.coverageType || onnetOffers.coverageType,
            offers: {
                tripleplay_modular: onnetOffers
                    ? onnetOffers.offers.tripleplay_modular
                    : null,
                residencial_modular: onnetOffers
                    ? onnetOffers.offers.residencial_modular
                    : null,
                tv_modular: onnetOffers ? onnetOffers.offers.tv_modular : null,
                izzimovil_modular: onnetOffers
                    ? onnetOffers.offers.izzimovil_modular
                    : null,
                izzinegocios: onnetOffers
                    ? onnetOffers.offers.izzinegocios
                    : null,
                izzinegocios_3P: onnetOffers
                    ? onnetOffers.offers.izzinegocios_3P
                    : null,
                offnet_izzi: offnetOffers
                    ? offnetOffers.offers.offnet_izzi
                    : null,
                offnet_sky: offnetOffers
                    ? offnetOffers.offers.offnet_sky
                    : null,
                offnet_izzi_movil: offnetOffers
                    ? offnetOffers.offers.offnet_izzi_movil
                    : null,
            },
        };
    };

    const handleFetchByPostalCode = async (event) => {
        event.preventDefault();
        if (fields.postalCode.error) {
            return;
        }
        const valid = formValidation(fields, {
            address,
            exterior,
            interior,
            neighborhood,
            postalCode,
            city,
            state,
        });
        fields = fields;
        if (!valid) {
            return;
        }
        try {
            errorMessage = '';
            $store.requestedAddress.offerError = false;
            const headers = new Headers();
            headers.append('x-api-key', env?.[env?.stage]?.api?.key);
            headers.append('Content-Type', 'application/json');
            isLoading = true;
            const validationData = await validateOffer(
                postalCode,
                marker.getPosition().lat(),
                marker.getPosition().lng()
            );

            if (!validationData.onnet && !validationData.offnet) {
                $store.requestedAddress.offerError = true;
                console.error('Error fetching offers:', error);
                errorMessage =
                    'Aún no llegamos a tu zona, pero seguimos creciendo. Intenta con otra dirección, por favor.';
                scrollToTop();
                isLoading = false;
                await init();
                return;
            }

            const requests = [
                fetch(
                    env[env.stage].api.url +
                        'apikey/izzi/ms/purchaseServices/sales/offersByCP',
                    {
                        method: 'POST',
                        headers: headers,
                        body: JSON.stringify({
                            postalCode,
                            offnet: validationData.offnet,
                            latitude: marker.getPosition().lat(),
                            longitude: marker.getPosition().lng(),
                        }),
                    }
                ),
            ];

            if (validationData.onnet) {
                requests.push(
                    fetch(
                        env[env.stage].api.url +
                            'apikey/izzi/ms/purchaseServices/sales/offersByCP',
                        {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify({
                                postalCode,
                                offnet: !validationData.onnet,
                                latitude: marker.getPosition().lat(),
                                longitude: marker.getPosition().lng(),
                            }),
                        }
                    )
                );
            }

            const fetchOffer = await Promise.all(requests);

            // check if any of the requests failed
            const failedRequests = fetchOffer.filter((res) => !res.ok);
            if (failedRequests.length > 0) {
                throw new Error('Error fetching offers');
            }

            const offerData = await Promise.all(
                fetchOffer.map((res) => res.json())
            );

            $store.requestedAddress.city = city;
            $store.requestedAddress.street = address;
            $store.requestedAddress.suburb = neighborhood;
            $store.requestedAddress.state = state;
            $store.requestedAddress.internalNumber = interior;
            $store.requestedAddress.externalNumber = exterior;
            $store.requestedAddress.offnetCoverage =
                validationData.onnet == false;
            $store.requestedAddress.latitude = marker.getPosition().lat();
            $store.requestedAddress.longitude = marker.getPosition().lng();
            $store.requestedAddress.postalCode = postalCode;
            $store.requestedAddress.municipality = city;

            if (interior) {
                $store.requestedAddress.addressString = `${address} ${exterior}, ${interior}, ${neighborhood}`;
            } else {
                $store.requestedAddress.addressString = `${address} ${exterior}, ${neighborhood}`;
            }

            if (offerData) {
                proxyOffer(offerData);
                // $store_offer.loadedOffer = offerData;
                scrollToTop();

                isLoading = false;
                toggleEditAddress();
            }
        } catch (error) {
            console.error('Error fetching offers:', error);
            errorMessage =
                'Aún no llegamos a tu zona, pero seguimos creciendo. Intenta con otra dirección, por favor.';
            scrollToTop();
            isLoading = false;
            await init();
        }
    };

    const handleAddressChange = (event) => {
        address = event.target.value;
        renderSpecificFields = true;
        if (address === '') {
            renderSpecificFields = false;
        }
    };

    const loadAddressFromStore = () => {
        if (!$store.requestedAddress.addressString) return;
        renderSpecificFields = true;
        city = $store.requestedAddress.city;
        address = $store.requestedAddress.street;
        neighborhood = $store.requestedAddress.suburb;
        state = $store.requestedAddress.state;
        interior = $store.requestedAddress.internalNumber;
        exterior = $store.requestedAddress.externalNumber;
        postalCode = $store.requestedAddress.postalCode;
        city = $store.requestedAddress.municipality;

        const coordinates = {
            lat: $store.requestedAddress.latitude,
            lng: $store.requestedAddress.longitude,
        };
        placeMarker(coordinates);
    };
</script>

<FullScreenLoader {isLoading} isIzziLogo={true} />
<main class="main-map-section">
    <div class="main-map-data-container">
        <div class="main-map-header">
            <!-- Only show this when there is an address already loaded -->
            {#if $store.requestedAddress.addressString}
                <a
                    href="#/"
                    class="back-link"
                    on:click|preventDefault={toggleEditAddress}
                    ><svg class="icon"
                        ><use
                            xlink:href="/assets/img/icons/symbol-defs.svg#icon-angle-left"
                        ></use></svg
                    > <span>Regresar a ofertas</span></a
                >
            {/if}
            <h1 class="main-map-title">
                descubre los servicios de izzi disponibles en tu zona
            </h1>
            <p class="main-map-description">
                ingresa tu dirección y te mostraremos los <span class="fw-bold"
                    >paquetes y promociones</span
                > que puedes contratar.
            </p>
            <form on:submit={handleFetchByPostalCode} class="main-map-form">
                {#if renderSpecificFields}
                    <div class="field-separator">
                        <MapInput
                            id="autocompleteCP"
                            placeholder="Código postal"
                            bind:value={postalCode}
                            maxLength="5"
                            label="Código postal"
                            name="autocompleteCP"
                            labelClass="placeholder placeholder-filled"
                            error={fields.postalCode.error}
                            errorMessage={fields.postalCode.errorMessage}
                            className="postal-code"
                            type="number"
                        />
                        <MapInput
                            id="address"
                            placeholder="Dirección"
                            bind:value={address}
                            label="Calle"
                            name="address"
                            labelClass="placeholder placeholder-filled"
                            error={fields.address.error}
                            errorMessage={fields.address.errorMessage}
                            className="address-input"
                        />
                    </div>
                    <div class="field-separator">
                        <div class="number-input">
                            <MapInput
                                id="Exterior"
                                placeholder="Exterior"
                                label="Exterior"
                                name="Exterior"
                                bind:value={exterior}
                                labelClass="placeholder placeholder-filled"
                                error={fields.exterior.error}
                                errorMessage={fields.exterior.errorMessage}
                            />
                            <MapInput
                                id="Interior"
                                placeholder="Opcional"
                                label="Interior"
                                name="Interior"
                                bind:value={interior}
                                labelClass="placeholder placeholder-filled"
                                error={fields.interior.error}
                                errorMessage={fields.interior.errorMessage}
                            />
                        </div>
                        <MapInput
                            id="neighborhood"
                            placeholder="Colonia"
                            bind:value={neighborhood}
                            label="Colonia"
                            name="neighborhood"
                            labelClass="placeholder placeholder-filled"
                            error={fields.neighborhood.error}
                            errorMessage={fields.neighborhood.errorMessage}
                            className="neighborhood-input"
                        />
                    </div>

                    <div class="field-separator">
                        <MapInput
                            id="city"
                            placeholder="Delegación o municipio"
                            bind:value={city}
                            label="Delegación o municipio"
                            name="city"
                            labelClass="placeholder placeholder-filled"
                            error={fields.city.error}
                            errorMessage={fields.city.errorMessage}
                            className="half-input"
                            disabled
                        />
                        <MapInput
                            id="state"
                            placeholder="Estado"
                            bind:value={state}
                            label="Estado"
                            name="state"
                            labelClass="placeholder placeholder-filled"
                            error={fields.state.error}
                            errorMessage={fields.state.errorMessage}
                            className="half-input"
                            disabled
                        />
                    </div>
                {:else}
                    <MapInput
                        id="autocompleteAddress"
                        placeholder="Dirección"
                        value={address}
                        label="Dirección"
                        name="autocompleteAddress"
                        on:change={handleAddressChange}
                        labelClass="placeholder placeholder-filled"
                    />
                {/if}

                <div class="main-map-buttons">
                    <button
                        class="pure__button pure__button--base"
                        disabled={address === ''}
                        on:click={handleFetchByPostalCode}
                        type="submit">Confirmar dirección</button
                    >
                    <div>
                        <button
                            class="pure__button location-button"
                            on:click={handleCurrentPosition}
                            type="button"
                        >
                            <svg
                                width="25"
                                height="24"
                                viewBox="0 0 25 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M12.5 4C11.1156 4 9.76219 4.41053 8.61105 5.17969C7.45991 5.94884 6.56271 7.04207 6.03288 8.32113C5.50306 9.60019 5.36441 11.0076 5.63448 12.3655C5.90454 13.7233 6.57115 14.9706 7.55003 15.9495C7.54999 15.9495 7.55006 15.9496 7.55003 15.9495L11.794 20.1925C11.8867 20.2853 11.9972 20.3593 12.1184 20.4095C12.2395 20.4598 12.3694 20.4856 12.5005 20.4856C12.6316 20.4856 12.7615 20.4598 12.8827 20.4095C13.0038 20.3593 13.1139 20.2857 13.2065 20.193L17.4499 15.9496C17.4499 15.9496 17.4499 15.9496 17.4499 15.9496C18.4288 14.9706 19.0955 13.7233 19.3655 12.3655C19.6356 11.0076 19.497 9.60019 18.9671 8.32113C18.4373 7.04207 17.5401 5.94884 16.389 5.17969C15.2378 4.41053 13.8845 4 12.5 4ZM7.49992 3.51674C8.97995 2.52783 10.72 2 12.5 2C14.28 2 16.0201 2.52783 17.5001 3.51674C18.9801 4.50565 20.1337 5.91123 20.8149 7.55574C21.4961 9.20025 21.6743 11.0098 21.3271 12.7556C20.9799 14.5015 20.1228 16.1051 18.8641 17.3638L14.6215 21.6065C14.6213 21.6066 14.6216 21.6063 14.6215 21.6065C14.3431 21.885 14.0123 22.1063 13.6485 22.2571C13.2845 22.408 12.8945 22.4856 12.5005 22.4856C12.1066 22.4856 11.7165 22.408 11.3525 22.2571C10.9886 22.1063 10.658 21.8852 10.3795 21.6065L6.13598 17.3639C4.87735 16.1052 4.02013 14.5015 3.6729 12.7556C3.32567 11.0098 3.50393 9.20025 4.18513 7.55574C4.86633 5.91123 6.01989 4.50565 7.49992 3.51674Z"
                                    fill="#262624"
                                />
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M12.5 9C11.9696 9 11.4609 9.21071 11.0858 9.58579C10.7107 9.96086 10.5 10.4696 10.5 11C10.5 11.5304 10.7107 12.0391 11.0858 12.4142C11.4609 12.7893 11.9696 13 12.5 13C13.0304 13 13.5391 12.7893 13.9142 12.4142C14.2893 12.0391 14.5 11.5304 14.5 11C14.5 10.4696 14.2893 9.96086 13.9142 9.58579C13.5391 9.21071 13.0304 9 12.5 9ZM9.67157 8.17157C10.4217 7.42143 11.4391 7 12.5 7C13.5609 7 14.5783 7.42143 15.3284 8.17157C16.0786 8.92172 16.5 9.93913 16.5 11C16.5 12.0609 16.0786 13.0783 15.3284 13.8284C14.5783 14.5786 13.5609 15 12.5 15C11.4391 15 10.4217 14.5786 9.67157 13.8284C8.92143 13.0783 8.5 12.0609 8.5 11C8.5 9.93913 8.92143 8.92172 9.67157 8.17157Z"
                                    fill="#262624"
                                />
                            </svg>Utilizar mi ubicación actual</button
                        >
                    </div>
                </div>
                {#if errorGeolocation}
                    <div class="warning-label">
                        <svg
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M7.99996 4.49325L13.02 13.1666H2.97996L7.99996 4.49325ZM7.99996 1.83325L0.666626 14.4999H15.3333L7.99996 1.83325ZM8.66663 11.1666H7.33329V12.4999H8.66663V11.1666ZM8.66663 7.16659H7.33329V9.83325H8.66663V7.16659Z"
                                fill="#D90527"
                            />
                        </svg>
                        <p>{errorGeolocation}</p>
                    </div>
                {/if}
            </form>

            {#if errorMessage}
                <p class="main-map-error">{errorMessage}</p>
            {/if}

            <p class="legend">
                Escribe tu dirección manualmente o usa el botón de 'Utilizar mi
                ubicación actual' para localizar tu dirección en el mapa. Mueve
                el pin para mayor precisión.
            </p>
        </div>
    </div>

    <div class="main-map-container">
        <div id="map" class="main-map"></div>
    </div>
</main>
